import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { Icon, Wordmark } from '@/app/kit';
import type { IconName } from '@/app/module';
import { TONE_BG, type Tone } from './tones';

/**
 * Empty screen or list with a funny line and one action.
 *
 *   <EmptyState art="trophy" title="No tournaments" body="Make one, crown someone." action={<Button>New</Button>} />
 */
export interface EmptyStateProps {
  title: string;
  body?: string;
  /** The loud stack logo, the sticker logo, an icon on a butter tile, or nothing. */
  art?: 'stack' | 'sticker' | IconName | 'none';
  action?: ReactNode;
  tone?: Tone;
  className?: string;
}

export function EmptyState({ title, body, art = 'stack', action, tone = 'white', className }: EmptyStateProps) {
  return (
    <section
      className={clsx(
        'flex flex-col items-center gap-4 rounded-tts border-(length:--tts-border) border-ink px-5 py-7 text-center shadow-hard',
        TONE_BG[tone],
        className,
      )}
    >
      {art === 'stack' || art === 'sticker' ? (
        <Wordmark variant={art} width={art === 'stack' ? 150 : 120} />
      ) : art !== 'none' ? (
        <span className="grid size-16 place-items-center rounded-tts border-3 border-ink bg-butter text-ink shadow-btn">
          <Icon name={art} size={34} fill="var(--color-white)" />
        </span>
      ) : null}
      <h2 className="tts-display text-2xl">{title}</h2>
      {body && <p className="max-w-[32ch] text-base font-medium text-ink/80">{body}</p>}
      {action}
    </section>
  );
}
